/**
 * Iter XX.192 — In-app AI-response reporting (Google Play generative-AI
 * policy requirement).
 *
 * Small, unobtrusive dialog rendered on demand from a "Report" link
 * next to any assistant message. Anonymous reports allowed (guest
 * chat is the primary experience). One backend call. No routing, no
 * page change, no clutter in the conversation UI when idle.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const CATEGORIES = [
  { value: "inappropriate", label: "Inappropriate or offensive" },
  { value: "harmful", label: "Harmful or unsafe" },
  { value: "incorrect", label: "Incorrect or misleading" },
  { value: "other", label: "Something else" },
];

export function ReportAIResponseDialog({
  open, onClose, sessionId, messageRef, reportedText, precedingUserText,
}) {
  const [category, setCategory] = useState("inappropriate");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/report/ai-response", {
        session_id: sessionId || null,
        message_ref: messageRef || null,
        category,
        note: note.slice(0, 400) || null,
        reported_ai_text: (reportedText || "").slice(0, 2000),
        preceding_user_text: (precedingUserText || "").slice(0, 500) || null,
      });
      if (data?.throttled) {
        toast("You've reported a few recently — we're on it.");
      } else {
        toast.success("Thanks — we'll review this.");
      }
      onClose && onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Couldn't send that report: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="report-ai-dialog"
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-neutral-950 border border-neutral-800 rounded-lg p-5 space-y-4"
      >
        <div>
          <h2 className="font-display text-lg text-neutral-100">
            Report this response
          </h2>
          <p className="text-xs text-neutral-500 mt-1">
            Tell us what's wrong. We keep only what's needed to
            investigate.
          </p>
        </div>

        <div className="space-y-2">
          {CATEGORIES.map((c) => (
            <label
              key={c.value}
              className="flex items-center gap-3 text-sm text-neutral-200 cursor-pointer"
            >
              <input
                data-testid={`report-ai-category-${c.value}`}
                type="radio"
                name="report_category"
                value={c.value}
                checked={category === c.value}
                onChange={() => setCategory(c.value)}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>

        <div className="space-y-1">
          <label
            htmlFor="report-ai-note"
            className="block text-xs text-neutral-500"
          >
            Optional detail (max 400 characters)
          </label>
          <textarea
            id="report-ai-note"
            data-testid="report-ai-note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 400))}
            rows={3}
            className="w-full bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-neutral-700"
            placeholder="What made this response feel wrong?"
          />
          <div className="text-[10px] text-neutral-600 text-right">
            {note.length}/400
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            data-testid="report-ai-cancel"
            onClick={onClose}
            className="text-sm text-neutral-400 hover:text-neutral-200 px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="report-ai-submit"
            disabled={submitting}
            className={`text-sm px-4 py-2 rounded font-medium ${
              submitting
                ? "bg-neutral-800 text-neutral-500"
                : "bg-emerald-700 hover:bg-emerald-600 text-white"
            }`}
          >
            {submitting ? "Sending…" : "Send report"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ReportAIResponseTrigger({
  sessionId, messageRef, reportedText, precedingUserText,
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="report-ai-trigger"
        onClick={() => setOpen(true)}
        className="text-[10px] uppercase tracking-wider text-neutral-600 hover:text-neutral-400 mt-1"
        aria-label="Report this AI response"
      >
        Report
      </button>
      <ReportAIResponseDialog
        open={open}
        onClose={() => setOpen(false)}
        sessionId={sessionId}
        messageRef={messageRef}
        reportedText={reportedText}
        precedingUserText={precedingUserText}
      />
    </>
  );
}
